import { Loader, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusPage from '../components/StatusPage';
import { paymentAPI } from '../services/api';
import { responseData } from '../services/apiResponse';

const OnrampReturn = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Returning to your transfer status...');
  const [transactionId, setTransactionId] = useState(null);
  const [requestId, setRequestId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkReturnStatus = async () => {
      const returnTransactionId = transactionIdFromReturnURL(window.location.search);
      const storedTransactionId = returnTransactionId || localStorage.getItem('remittance_transaction_id');
      const storedRequestId = localStorage.getItem('request_id');
      setTransactionId(storedTransactionId);
      setRequestId(storedRequestId);

      if (!storedTransactionId) {
        navigate('/', { replace: true });
        return;
      }
      if (returnTransactionId) {
        localStorage.setItem('remittance_transaction_id', returnTransactionId);
      }

      const returnStatus = providerReturnStatusFromURL();

      try {
        let resolvedTransactionId = storedTransactionId;
        if (storedRequestId) {
          try {
            const requestResponse = await paymentAPI.getPaymentRequest(storedRequestId);
            const candidateId = resolveReturnTransactionId(responseData(requestResponse), resolvedTransactionId);
            if (candidateId !== resolvedTransactionId) {
              resolvedTransactionId = candidateId;
              localStorage.setItem('remittance_transaction_id', resolvedTransactionId);
              setTransactionId(resolvedTransactionId);
            }
          } catch {
            // Keep the original attempt result when request reconciliation is unavailable.
          }
        }

        const response = await paymentAPI.getPaymentStatus(resolvedTransactionId);
        if (cancelled) return;

        const transactionData = responseData(response);
        if (isTerminalFailureStatus(transactionData?.status)) {
          setStatus('failed');
          setMessage(transactionData?.readable_status || 'Payment was not completed.');
          return;
        }

        navigate(`/success/${resolvedTransactionId}`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        if (isProviderFailureStatus(returnStatus)) {
          setStatus('failed');
          setMessage(returnStatus === 'expired' ? 'Payment session expired.' : 'Payment was not completed.');
          return;
        }
        setStatus('checking');
        setMessage('We could not confirm the latest provider status yet.');
        navigate(`/success/${storedTransactionId}`, { replace: true });
      }
    };

    checkReturnStatus();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (status === 'failed') {
    return (
      <StatusPage
        variant="error"
        title={message || 'Payment Not Completed'}
        description="Your provider returned without a completed payment."
        step={{ current: 4, total: 5 }}
        onBack={() => navigate(requestId ? `/review/${requestId}` : '/')}
        actions={[
          {
            label: 'Retry',
            icon: RefreshCw,
            primary: true,
            onClick: () => navigate(requestId ? `/onramp-checkout/${requestId}` : `/success/${transactionId}`),
          },
        ]}
      />
    );
  }

  return (
    <StatusPage
      variant="warning"
      title="Checking Payment"
      description={message}
      step={{ current: 4, total: 5 }}
      onBack={() => navigate(requestId ? `/review/${requestId}` : '/')}
    >
      <div className="flex justify-center py-2">
        <Loader className="w-5 h-5 text-primary-600 animate-spin" />
      </div>
    </StatusPage>
  );
};

export function resolveReturnTransactionId(request, currentTransactionId) {
  const successfulAttemptId = String(request?.successful_attempt_id || '').trim();
  if (successfulAttemptId && successfulAttemptId !== currentTransactionId) return successfulAttemptId;

  const activeAttemptId = String(request?.active_attempt_id || '').trim();
  if (activeAttemptId && activeAttemptId !== currentTransactionId) return activeAttemptId;

  return currentTransactionId;
}

export function transactionIdFromReturnURL(search) {
  const params = new URLSearchParams(search || '');
  return String(
    params.get('transaction_id') ||
      params.get('transactionId') ||
      params.get('remittance_transaction_id') ||
      '',
  ).trim();
}

function isTerminalFailureStatus(status) {
  return ['failed', 'cancelled', 'canceled', 'refunded', 'expired'].includes(
    String(status || '').toLowerCase(),
  );
}

function providerReturnStatusFromURL() {
  const params = new URLSearchParams(window.location.search);
  const candidates = [
    params.get('status'),
    params.get('transactionStatus'),
    params.get('paymentStatus'),
    params.get('result'),
    params.get('state'),
    params.get('error'),
    params.get('event'),
  ];
  return (candidates.find(Boolean) || '').toLowerCase();
}

function isProviderFailureStatus(status) {
  return ['failed', 'failure', 'cancelled', 'canceled', 'error', 'declined', 'expired'].includes(status);
}

export default OnrampReturn;
